/**
 * BRE-8006: keeps an invitation through a Google/Microsoft/SSO round trip.
 * A sign-in that completes lands on `/invite` via the OAuth `state`; one the
 * provider refuses comes back to /auth/callback with no `state`, so the token
 * also rides a short-lived cookie scoped to the callback path.
 *
 * Imported by the edge middleware and by client components: no Node APIs.
 */
import { sanitizeReturnTo } from "./return-to";

export const INVITATION_TOKEN_PARAM = "invitation_token";

export const OAUTH_INVITATION_COOKIE = "brev_oauth_invitation";

/** Long enough to sit on a provider's account chooser or an admin-approval screen. */
export const OAUTH_INVITATION_COOKIE_MAX_AGE_SECONDS = 30 * 60;

/** Only /auth/callback needs it back. */
const OAUTH_INVITATION_COOKIE_PATH = "/auth/callback";

const MAX_INVITATION_TOKEN_LENGTH = 256;
const INVITATION_TOKEN_SHAPE = /^[A-Za-z0-9._~-]+$/;

/** Shape only, so nothing else is reflected into a cookie or link; WorkOS decides whether it is real. */
export function isPlausibleInvitationToken(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length > 0 &&
    value.length <= MAX_INVITATION_TOKEN_LENGTH &&
    INVITATION_TOKEN_SHAPE.test(value)
  );
}

/**
 * Where an OAuth or SSO sign-in started from an auth page lands. An invitation
 * wins over `returnTo`: `/invite` is what answers it for a signed-in invitee.
 */
export function oauthLandingPath(opts: { invitationToken?: string; returnTo?: string }): string | undefined {
  if (isPlausibleInvitationToken(opts.invitationToken)) {
    const params = new URLSearchParams({ [INVITATION_TOKEN_PARAM]: opts.invitationToken });
    return `/invite?${params.toString()}`;
  }
  return sanitizeReturnTo(opts.returnTo) ?? undefined;
}

/** The `Set-Cookie` value that parks `token` for the callback, or clears it when `token` is null. */
export function oauthInvitationCookie(token: string | null, secure: boolean): string {
  const attributes = `Path=${OAUTH_INVITATION_COOKIE_PATH}; HttpOnly; SameSite=Lax${secure ? "; Secure" : ""}`;
  return token === null
    ? `${OAUTH_INVITATION_COOKIE}=; Max-Age=0; ${attributes}`
    : `${OAUTH_INVITATION_COOKIE}=${token}; Max-Age=${OAUTH_INVITATION_COOKIE_MAX_AGE_SECONDS}; ${attributes}`;
}
